import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ExportButtons = ({ filters, type = 'shown', token, baseUrl }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async (format) => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}api/llm-agent/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filters, type, format })
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `drug-export-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export-buttons">
      <button
        onClick={() => handleExport('csv')}
        disabled={loading}
        className="export-btn"
      >
        📊 Export CSV
      </button>
      <button
        onClick={() => handleExport('pdf')}
        disabled={loading}
        className="export-btn"
      >
        📄 Export PDF
      </button>
    </div>
  );
};

const ChatWindow = ({
  activeChat,
  conversationName,
  messages,
  onSend,
  loading,
  isGenerating,
  isExporting,
  onExportPdf,
  token,
  baseUrl
}) => {
  const [input, setInput] = useState("");
  const [showExamples, setShowExamples] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeChat]);

  const handleSubmit = (val) => {
    const text = val || input;
    if (!text.trim()) return;
    onSend(text.trim());
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getDateLabel = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return date.toLocaleDateString("en-GB");
  };

  let lastDate = null;

  return (
    <div className="chat-window">
      {activeChat ? (
        <>
          <div className="chat-header">
            <div className="chat-info">
              <h3>{conversationName || activeChat}</h3>
              <p>{messages?.length || 0} messages</p>
            </div>
            <button
              className="export-btn"
              onClick={onExportPdf}
              disabled={isExporting || (messages?.length === 0)}
              title="Export to PDF"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {isExporting ? "Exporting..." : "Export PDF"}
            </button>
          </div>

          <div className="messages">
            {messages?.flatMap((msg, index) => {
              const messageDate = getDateLabel(
                msg.publishedAt || msg.updatedAt || msg.createdAt
              );

              const elements = [];

              if (messageDate !== lastDate) {
                elements.push(
                  <div key={`date-${index}`} className="date-separator">
                    {messageDate}
                  </div>
                );
                lastDate = messageDate;
              }

              // User message
              if (msg.prompt) {
                elements.push(
                  <div key={msg._localId || `${index}-user`} className="user-msg-wrapper">
                    <div className="user-msg">
                      {msg.prompt}
                      <div className="msg-time">
                        {formatTime(msg.publishedAt || msg.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              }

              // AI message
              if (msg.response) {
                elements.push(
                  <div key={msg._localId || `${index}-ai`} className="ai-msg-wrapper">
                    <div className="ai-msg">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.response}
                      </ReactMarkdown>
                      {msg.exportMetadata && (
                        <ExportButtons
                          filters={msg.exportMetadata.filters}
                          type="shown"
                          token={token}
                          baseUrl={baseUrl}
                        />
                      )}
                      {isGenerating && index === messages.length - 1 && (
                        <div className="generating-indicator">
                          Generating<span className="dots"></span>
                        </div>
                      )}
                      <div className="msg-time">
                        {formatTime(msg.updatedAt)}
                      </div>
                    </div>
                  </div>
                );
              }

              return elements;
            })}

            {loading && (
              <div className="ai-msg-wrapper">
                <div className="ai-msg typing-loader">...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="example-prompts-section">
            <div className="example-prompts-toggle" onClick={() => setShowExamples(!showExamples)}>
              <span className="toggle-label">Examples</span>
              <span className="toggle-text">
                {showExamples ? 'Hide example prompts...' : 'Show example prompts...'}
              </span>
              <span className="toggle-icon">{showExamples ? '▼' : '▶'}</span>
            </div>

            {showExamples && (
              <div className="example-prompts-grid">
                <div className="example-prompt-card" onClick={() => { setInput("Find drug repositioning opportunities for breast cancer targeting PI3K/AKT pathway"); setShowExamples(false); }}>
                  <div className="card-icon-left">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" />
                      <line x1="9" y1="6" x2="15" y2="6" />
                      <line x1="9" y1="10" x2="15" y2="10" />
                      <line x1="9" y1="14" x2="15" y2="14" />
                    </svg>
                  </div>
                  <div className="card-text">
                    <div className="prompt-title">Drug Discovery</div>
                    <div className="prompt-description">
                      Find drug repositioning opportunities for breast cancer targeting PI3K/AKT pathway
                    </div>
                  </div>
                </div>

                <div className="example-prompt-card" onClick={() => { setInput("Generate a protein-protein interaction network for NSCLC using TCGA bulk tumor samples with p-value < 0.01"); setShowExamples(false); }}>
                  <div className="card-icon-left">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <circle cx="6" cy="6" r="2" />
                      <circle cx="18" cy="6" r="2" />
                      <circle cx="6" cy="18" r="2" />
                      <circle cx="18" cy="18" r="2" />
                    </svg>
                  </div>
                  <div className="card-text">
                    <div className="prompt-title">Network Analysis</div>
                    <div className="prompt-description">
                      Generate a protein-protein interaction network for NSCLC using TCGA bulk tumor samples with p-value &lt; 0.01
                    </div>
                  </div>
                </div>

                <div className="example-prompt-card" onClick={() => { setInput("Identify prognostic or predictive biomarkers for a given cancer type using gene expression and survival data."); setShowExamples(false); }}>
                  <div className="card-icon-left">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeLinejoin="round" />
                      <path d="M2 17L12 22L22 17" strokeLinejoin="round" />
                      <path d="M2 12L12 17L22 12" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="card-text">
                    <div className="prompt-title">Biomarker Browser</div>
                    <div className="prompt-description">
                      Identify prognostic or predictive biomarkers for a given cancer type using gene expression and survival data.
                    </div>
                  </div>
                </div>

                <div className="example-prompt-card" onClick={() => { setInput("Explore SEER cohort relationships between treatments, stages, and survival outcomes for a selected cancer."); setShowExamples(false); }}>
                  <div className="card-icon-left">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="card-text">
                    <div className="prompt-title">SEER Corelation</div>
                    <div className="prompt-description">
                      Explore SEER cohort relationships between treatments, stages, and survival outcomes for a selected cancer.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="input-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe your genomic analysis needs... (e.g., 'Generate PPI network for NSCLC with p < 0.01')"
              onKeyDown={handleKeyDown}
            />
            <button onClick={() => handleSubmit()}>Send</button>
          </div>
        </>
      ) : (
        <div className="chat-window-welcome">
          <div className="welcome-content-centered">
            <div className="welcome-header">
              <h2>Welcome to SOSTOS AI Agent</h2>
              <p className="welcome-subtitle">
                Ask questions about cancer, drugs, biomarkers, and network-level relationships. Start with one of the
                curated example prompts below or describe your own workflow.
              </p>
            </div>

            <div className="welcome-cards-grid">
              <div className="example-prompt-card" onClick={() => handleSubmit("Find drug repositioning opportunities for breast cancer targeting PI3K/AKT pathway")}>
                <div className="card-icon-left">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="9" y1="6" x2="15" y2="6" />
                    <line x1="9" y1="10" x2="15" y2="10" />
                    <line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                </div>
                <div className="card-text">
                  <div className="prompt-title">Drug Discovery</div>
                  <div className="prompt-description">
                    Find drug repositioning opportunities for breast cancer targeting PI3K/AKT pathway
                  </div>
                </div>
              </div>

              <div className="example-prompt-card" onClick={() => handleSubmit("Generate a protein-protein interaction network for NSCLC using TCGA bulk tumor samples with p-value < 0.01")}>
                <div className="card-icon-left">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <circle cx="6" cy="6" r="2" />
                    <circle cx="18" cy="6" r="2" />
                    <circle cx="6" cy="18" r="2" />
                    <circle cx="18" cy="18" r="2" />
                  </svg>
                </div>
                <div className="card-text">
                  <div className="prompt-title">Network Analysis</div>
                  <div className="prompt-description">
                    Generate a protein-protein interaction network for NSCLC using TCGA bulk tumor samples with p-value &lt; 0.01
                  </div>
                </div>
              </div>

              <div className="example-prompt-card" onClick={() => handleSubmit("Identify prognostic or predictive biomarkers for a given cancer type using gene expression and survival data.")}>
                <div className="card-icon-left">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeLinejoin="round" />
                    <path d="M2 17L12 22L22 17" strokeLinejoin="round" />
                    <path d="M2 12L12 17L22 12" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="card-text">
                  <div className="prompt-title">Biomarker Browser</div>
                  <div className="prompt-description">
                    Identify prognostic or predictive biomarkers for a given cancer type using gene expression and survival data.
                  </div>
                </div>
              </div>

              <div className="example-prompt-card" onClick={() => handleSubmit("Explore SEER cohort relationships between treatments, stages, and survival outcomes for a selected cancer.")}>
                <div className="card-icon-left">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="card-text">
                  <div className="prompt-title">SEER Corelation</div>
                  <div className="prompt-description">
                    Explore SEER cohort relationships between treatments, stages, and survival outcomes for a selected cancer.
                  </div>
                </div>
              </div>
            </div>

            <div className="welcome-input-centered">
              <div className="input-row-welcome">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder='Describe your genomic analysis needs... (e.g., "Generate PPI network for NSCLC with p < 0.01")'
                  onKeyDown={handleKeyDown}
                />
                <button className="send-btn-welcome" onClick={() => handleSubmit()}>
                  Send
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
